// src/encounter/lab-add-v3-parts.tsx
// The Add lab popup's pieces on the agreed lab model (PROD-2849), drawn to canvas boards 6-9
// (AddLabAtomic, AddLabHouse, AddLabDuplicate, EdgeStates). Presentational only: the rules are in
// lib/lab-order-v3.ts and the state in lab-add-dialog-v3.tsx.
//
//   · LabTypeChip      "Panel" (blue) · "Single test" / "Clinic panel" (grey), 16px tall.
//   · LabChip          "Lab: NRL" — a 26px button with a chevron that opens the lab menu (the allowed
//                      labs, "Preferred" under the preferred one, then "Patient's choice of lab"), or a
//                      fixed grey chip when the lab can't be changed.
//   · LabChips         the specimen and prep chips.
//   · LabV3Tests       the TESTS box: fixed, tickable, left-out, already-ordered, Patient's choice and
//                      reflex rows, and the line under the box.
//   · LabInfoLine      "Sent by the clinic to NRL (…)." on the grey panel with an info glyph.
//   · LabQuestions     QUESTIONS FROM THE LAB, one row per question (Yes/No, a choice, a time or text).
//   · LabSearchResults the results under the search field: the clinic's rows, or "Your clinic: No match
//                      … / Standard list" (board 9).
//
// Colours are DS tokens. Two board values have no token and use the nearest one (flagged in the PR):
// the already-ordered row's #FBFCFD (bg-grey-100) and the Patient's choice tag text #7A4A00
// (text-status-amber-text).
import { useId, useRef, useState } from 'react';
import {
  AnchoredPopover,
  AnchoredPopoverContent,
  AnchoredPopoverItem,
  AnchoredPopoverTrigger,
  Checkbox,
  Segmented,
  TextField,
} from '@scripsteam/scrips-signal-ds';
import { ChevronDown, Info, Repeat, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AP_FIELD_LABEL_CLASS } from './ap-dialog-label-style';
import {
  NO_CLINIC_MATCH,
  PATIENTS_CHOICE_LABEL,
  PATIENTS_CHOICE_MENU_HINT,
  labName,
  labTitle,
  questionKey,
  searchRowView,
  type InfoLine,
  type V3TestRow,
} from './lib/lab-order-v3';
import type { LabOrderLab, LabOrderQuestion, LabOrderSearchResult, LabOrderSearchItem } from './services/use-lab-order-search';

// ── Type chip ───────────────────────────────────────────────────────────────────────────────────

const TYPE_CHIP =
  'inline-flex h-4 items-center justify-center whitespace-nowrap rounded-sm px-1 text-size-eyebrow font-medium tracking-tight';
const TYPE_TONE = { blue: 'bg-blue-100 text-brand-depth', grey: 'bg-status-neutral-bg text-grey-800' } as const;

export function LabTypeChip({ label, tone }: { label: string; tone: 'blue' | 'grey' }) {
  return (
    <span className={cn(TYPE_CHIP, TYPE_TONE[tone])} data-testid="lab-type-chip">
      {label}
    </span>
  );
}

// ── Lab chip ────────────────────────────────────────────────────────────────────────────────────

const LAB_BUTTON =
  'inline-flex h-6.5 items-center gap-2 rounded-control border border-solid bg-surface-card pl-3 pr-2 font-sans text-size-eyebrow text-grey-800 cursor-pointer';
const LAB_FIXED = 'inline-flex h-6.5 items-center gap-2 rounded-control bg-grey-200 px-3 text-size-eyebrow text-grey-800';
const OPTION = 'flex w-full flex-col items-start rounded-control px-3 py-2 text-left';
const OPTION_HINT = 'text-size-xs text-text-muted';

export interface LabChipProps {
  /** The chip's text after "Lab: ". */
  text: string;
  /** The full name of the lab(s) in `text`, shown as a tooltip. */
  title?: string;
  /** The allowed labs; null = a fixed chip. */
  labs: readonly LabOrderLab[] | null;
  /** The chosen lab id, or null for Patient's choice of lab. */
  selectedLabId: string | null;
  onPickLab: (lab: LabOrderLab) => void;
  onPickPatientsChoice: () => void;
}

export function LabChip({ text, title, labs, selectedLabId, onPickLab, onPickPatientsChoice }: LabChipProps) {
  const [open, setOpen] = useState(false);
  // In place, not portalled to <body>, so the menu stays inside the modal's focus trap.
  const hostRef = useRef<HTMLSpanElement>(null);
  const label = (
    <>
      Lab:{' '}
      <strong className="font-semibold" title={title}>
        {text}
      </strong>
    </>
  );
  if (!labs) {
    return (
      <span className={LAB_FIXED} data-testid="lab-chip" data-fixed="true">
        {label}
      </span>
    );
  }
  const options: { id: string | null; name: string; title?: string; hint?: string; lab?: LabOrderLab }[] = [
    ...labs.map((l) => ({
      id: l.labVendorId,
      name: labName(l),
      title: labTitle(l) || undefined,
      hint: l.isPreferred ? 'Preferred' : undefined,
      lab: l,
    })),
    { id: null, name: PATIENTS_CHOICE_LABEL, hint: PATIENTS_CHOICE_MENU_HINT },
  ];
  return (
    <span ref={hostRef} className="relative inline-flex" data-testid="lab-chip">
      <AnchoredPopover open={open} onOpenChange={setOpen}>
        <AnchoredPopoverTrigger
          render={
            <button
              type="button"
              aria-haspopup="listbox"
              aria-expanded={open}
              className={cn(LAB_BUTTON, open ? 'border-brand-primary' : 'border-border-card')}
            >
              {label}
              <ChevronDown className="size-4 text-text-secondary" aria-hidden />
            </button>
          }
        />
        <AnchoredPopoverContent container={hostRef} align="end" aria-label="Lab" role="listbox" className="w-55">
          {options.map((o) => {
            const selected = o.id === selectedLabId;
            return (
              <AnchoredPopoverItem
                key={o.id ?? 'patients-choice'}
                role="option"
                aria-selected={selected}
                className={cn(OPTION, selected && 'bg-blue-100')}
                onClick={() => {
                  if (o.lab) onPickLab(o.lab);
                  else onPickPatientsChoice();
                  setOpen(false);
                }}
              >
                <span className={cn('text-size-sm text-text-primary', selected ? 'font-semibold' : 'font-normal')} title={o.title}>
                  {o.name}
                </span>
                {o.hint && <span className={OPTION_HINT}>{o.hint}</span>}
              </AnchoredPopoverItem>
            );
          })}
        </AnchoredPopoverContent>
      </AnchoredPopover>
    </span>
  );
}

// ── Chips ───────────────────────────────────────────────────────────────────────────────────────

const CHIP = 'inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-control bg-grey-200 px-2 text-size-eyebrow font-medium text-text-secondary';

export function LabChips({ chips }: { chips: readonly string[] }) {
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="lab-chips">
      {chips.map((c) => (
        <span key={c} className={CHIP}>
          {c}
        </span>
      ))}
    </div>
  );
}

// ── Tests ───────────────────────────────────────────────────────────────────────────────────────

const LIST = 'overflow-hidden rounded-md border border-solid border-border-subtle bg-surface-card';
const ROW = 'flex items-start gap-3 px-3 py-2 border-0 border-b border-solid border-b-border-subtle last:border-b-0';
const LINE = 'flex items-baseline justify-between gap-3';
const LOINC = 'text-size-eyebrow text-text-hint tabular-nums whitespace-nowrap';
const NOTE = 'mt-1 text-size-eyebrow leading-body text-text-secondary';
const UNDO = 'border-0 bg-transparent p-0 font-sans text-size-eyebrow text-brand-primary underline cursor-pointer';
const PC_TAG =
  'inline-flex h-4.5 items-center whitespace-nowrap rounded-sm bg-status-warning-bg px-2 text-size-xs font-medium text-status-amber-text';
const BELOW = 'm-0 mt-2 text-size-eyebrow leading-body text-text-secondary';

export function PatientsChoiceTag() {
  return <span className={PC_TAG}>{PATIENTS_CHOICE_LABEL}</span>;
}

function TestRow({ row, onToggle }: { row: V3TestRow; onToggle: (key: string) => void }) {
  const id = useId();
  const locked = row.state !== 'tick';
  const muted = row.state === 'already';
  return (
    <div className={cn(ROW, muted && 'bg-grey-100')} data-state={row.state} data-testid="lab-test-row">
      {row.state === 'reflex' ? (
        <span className="inline-flex w-4 shrink-0 justify-center pt-1 text-text-hint" aria-hidden="true">
          <Repeat className="size-4" aria-hidden />
        </span>
      ) : (
        <Checkbox
          id={id}
          aria-label={row.name}
          checked={row.checked}
          disabled={locked}
          onCheckedChange={() => {
            if (!locked) onToggle(row.key);
          }}
          className={cn('mt-px', locked && '!cursor-default')}
        />
      )}
      <div className="grow min-w-0">
        <div className={LINE}>
          <label
            htmlFor={row.state === 'reflex' ? undefined : id}
            className={cn('text-size-sm leading-reading', muted || row.state === 'reflex' ? 'text-text-muted' : 'text-text-primary', !locked && 'cursor-pointer')}
          >
            {row.name}
          </label>
          <span className="inline-flex items-baseline gap-2">
            {row.labName && (
              <span className="text-size-xs text-text-secondary" title={row.labTitle}>
                {row.labName}
              </span>
            )}
            {row.patientsChoice && <PatientsChoiceTag />}
            <span className={LOINC}>{row.loinc ?? '—'}</span>
          </span>
        </div>
        {row.subLine && <div className={NOTE}>{row.subLine}</div>}
        {row.note && (
          <div className={NOTE}>
            {row.note}
            {row.undo && (
              <>
                {' '}
                <button type="button" className={UNDO} onClick={() => onToggle(row.key)}>
                  Undo
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export interface LabV3TestsProps {
  header: string;
  rows: readonly V3TestRow[];
  onToggle: (key: string) => void;
  hint?: string | null;
  error?: string | null;
}

export function LabV3Tests({ header, rows, onToggle, hint, error }: LabV3TestsProps) {
  return (
    <div data-testid="lab-tests">
      <div className="mb-2 flex items-center justify-between">
        <span className={AP_FIELD_LABEL_CLASS}>{header}</span>
      </div>
      <div className={LIST} role="group" aria-label="Tests">
        {rows.map((r) => (
          <TestRow key={r.key} row={r} onToggle={onToggle} />
        ))}
      </div>
      {error ? (
        <p className={cn(BELOW, 'text-status-error')} data-testid="lab-tests-error">
          {error}
        </p>
      ) : (
        hint && (
          <p className={BELOW} data-testid="lab-tests-hint">
            {hint}
          </p>
        )
      )}
    </div>
  );
}

// ── Info line ───────────────────────────────────────────────────────────────────────────────────

export function LabInfoLine({ line }: { line: InfoLine }) {
  return (
    <div
      className="flex items-start gap-2 rounded-md bg-grey-100 px-3 py-2.5 text-size-eyebrow leading-body text-text-secondary"
      data-testid="lab-info-line"
    >
      <span className="mt-px inline-flex text-text-secondary" aria-hidden="true">
        <Info className="size-4" />
      </span>
      <span>
        {line.lead}
        {line.lab && <strong title={line.labTitle}>{line.lab}</strong>}
        {line.tail}
      </span>
    </div>
  );
}

// ── Questions from the lab ──────────────────────────────────────────────────────────────────────

export interface LabQuestionsProps {
  questions: readonly LabOrderQuestion[];
  answers: Readonly<Record<string, string>>;
  onAnswer: (key: string, value: string) => void;
}

function QuestionControl({ q, value, onChange }: { q: LabOrderQuestion; value: string; onChange: (v: string) => void }) {
  const type = (q.answerType ?? '').toLowerCase();
  if (type === 'yesno' || (!type && !(q.choices ?? []).length)) {
    return (
      <Segmented<string>
        aria-label={q.text}
        value={value}
        onChange={onChange}
        options={[
          { value: 'Yes', label: 'Yes' },
          { value: 'No', label: 'No' },
        ]}
      />
    );
  }
  if (type === 'choice' && (q.choices ?? []).length > 0) {
    return (
      <Segmented<string>
        aria-label={q.text}
        value={value}
        onChange={onChange}
        options={(q.choices ?? []).map((c) => ({ value: c, label: c }))}
      />
    );
  }
  return (
    <TextField
      aria-label={q.text}
      size="compact"
      type={type === 'time' ? 'time' : 'text'}
      value={value}
      maxLength={500}
      onChange={(e) => onChange(e.target.value)}
      containerClassName="w-40"
    />
  );
}

export function LabQuestions({ questions, answers, onAnswer }: LabQuestionsProps) {
  if (questions.length === 0) return null;
  return (
    <div className="flex flex-col gap-3" data-testid="lab-questions">
      {questions.map((q, i) => {
        const key = questionKey(q);
        return (
          <div key={key} className="flex items-center justify-between gap-3">
            <div className="flex flex-col gap-1">
              {i === 0 && <span className={AP_FIELD_LABEL_CLASS}>Questions from the lab</span>}
              <span className="text-size-sm text-text-primary">
                {q.text}
                {q.required && <span className="text-status-error"> *</span>}
              </span>
            </div>
            <QuestionControl q={q} value={answers[key] ?? ''} onChange={(v) => onAnswer(key, v)} />
          </div>
        );
      })}
    </div>
  );
}

// ── Search ──────────────────────────────────────────────────────────────────────────────────────

const RESULTS = 'overflow-hidden rounded-md border border-solid border-border-subtle bg-surface-card';
const GROUP = `block px-3 pt-3 ${AP_FIELD_LABEL_CLASS}`;
const RESULT_ROW =
  'flex w-full items-center justify-between gap-3 border-0 border-b border-solid border-b-border-subtle bg-transparent px-3 py-2.5 text-left font-sans last:border-b-0 cursor-pointer hover:bg-grey-100';

function ResultRow({ item, onPick }: { item: LabOrderSearchItem; onPick: (item: LabOrderSearchItem) => void }) {
  const v = searchRowView(item);
  return (
    <button type="button" role="option" aria-selected={false} className={RESULT_ROW} onClick={() => onPick(item)}>
      <span className="min-w-0">
        <span className="block text-size-sm text-text-primary">{v.name}</span>
        {v.subLine && <span className="block text-size-eyebrow text-text-muted">{v.subLine}</span>}
      </span>
      {v.patientsChoice ? <PatientsChoiceTag /> : v.labTag && (
          <span className="text-size-xs text-text-secondary" title={v.labTitle ?? undefined}>
            {v.labTag}
          </span>
        )}
    </button>
  );
}

export interface LabSearchResultsProps {
  result: LabOrderSearchResult;
  onPick: (item: LabOrderSearchItem) => void;
}

/** The results box. Null when there is nothing to list (the dialog shows its own "No lab tests match"). */
export function LabSearchResults({ result, onPick }: LabSearchResultsProps) {
  const clinic = result.items.filter((i) => !i.fromStandardList);
  const standard = result.items.filter((i) => i.fromStandardList);
  if (clinic.length === 0 && standard.length === 0) return null;
  const grouped = !!result.standardListSearched || standard.length > 0;
  return (
    <div className={RESULTS} data-testid="lab-search-results">
      {grouped && <span className={GROUP}>Your clinic</span>}
      {clinic.length === 0 ? (
        <div className="px-3 pt-2 pb-3 text-size-sm text-text-muted">{NO_CLINIC_MATCH}</div>
      ) : (
        <div role="listbox" aria-label="Your clinic">
          {clinic.map((i) => (
            <ResultRow key={i.id} item={i} onPick={onPick} />
          ))}
        </div>
      )}
      {standard.length > 0 && (
        <>
          <span className={GROUP}>Standard list</span>
          <div role="listbox" aria-label="Standard list">
            {standard.map((i) => (
              <ResultRow key={i.id} item={i} onPick={onPick} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** The search field: the DS TextField with the search glyph, 40px. */
export function LabSearchField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <TextField
      aria-label="Search lab orders"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Search lab orders…"
      leadingIcon={<Search className="size-4 text-text-hint" aria-hidden />}
    />
  );
}
